import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import clsx from 'clsx'

import { useBodyScrollLock } from '@/shared/hooks/useBodyScrollLock'

import { DismissButton } from './DismissButton'

type MobileSheetProps = {
  open: boolean
  onClose: () => void
  title?: string
  /** Acessibilidade quando `title` não é exibido no header. */
  ariaLabel?: string
  children: ReactNode
  /** Conteúdo fixo no rodapé, fora da área de scroll. */
  footer?: ReactNode
  className?: string
}

export const MobileSheet = ({
  open,
  onClose,
  title,
  ariaLabel,
  children,
  footer,
  className
}: MobileSheetProps) => {
  useBodyScrollLock(open)

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <>
      <button
        type="button"
        className="fixed inset-0 z-sheet-backdrop bg-black/55"
        aria-label="Fechar"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? title}
        className={clsx(
          'fixed inset-x-0 bottom-0 z-sheet mx-auto flex max-h-[85vh] w-full max-w-[500px] flex-col overflow-hidden rounded-t-xl bg-white shadow-xl',
          className
        )}
      >
        {/* Handle + close row — sempre visível */}
        <div className="relative flex shrink-0 items-center justify-center px-4 pb-2 pt-3">
          <div className="h-1 w-10 rounded-full bg-gray-200" />
          <DismissButton
            onClick={onClose}
            aria-label="Fechar"
            className="absolute right-3 top-2"
          />
        </div>

        {/* Título opcional */}
        {title ? (
          <div className="shrink-0 border-b border-gray-100 px-4 pb-3">
            <h2 className="m-0 line-clamp-2 text-center text-base font-bold leading-snug text-gray-900">
              {title}
            </h2>
          </div>
        ) : null}

        {/* Conteúdo rolável */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4">
          {children}
        </div>

        {/* Footer fixo */}
        {footer ? (
          <div className="shrink-0 border-t border-gray-100 bg-white px-4 pb-4 pt-3">
            {footer}
          </div>
        ) : null}
      </div>
    </>,
    document.body
  )
}
